import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Check, FileSignature, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  DEPENDENT_TYPES,
  DEPENDENT_TYPE_LABELS,
  MAINTENANCE_FREQUENCY_LABELS,
  MEMBER_STATUSES,
  MEMBER_STATUS_LABELS,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  formatCents,
  onlyDigits,
  type Member,
  type MemberDependentInput,
  type MemberPlan,
  type MemberStatus,
  type Paginated,
  type PaymentMethod,
  type Person,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { blank, formatDate, formatDateTime, formatDocument } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { ContextPanel, ReadField, StatTiles } from '@/components/ContextPanel';
import { StatusBadge, type BadgeKind } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog, Drawer, Modal } from '@/components/Modal';
import { CollapsibleSection, Field, FormSection } from '@/components/FormSection';
import { MoneyInput } from '@/components/MoneyInput';
import { EmptyState, ErrorState, NoResultsState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { useBankAccounts, todayISO } from '@/lib/queries';
import { DateInput } from '@/components/DateInput';

export const MEMBER_STATUS_TONE: Record<MemberStatus, BadgeKind> = {
  CONTRATO_PENDENTE: 'warn',
  ATIVO: 'ok',
  INADIMPLENTE: 'bad',
  INATIVO: 'mute',
  TRANSFERIDO: 'info',
  RESCINDIDO: 'bad',
};

/** Espelho das transições do servidor — decide quais botões aparecem. */
const TRANSITIONS: Record<MemberStatus, MemberStatus[]> = {
  CONTRATO_PENDENTE: ['ATIVO', 'RESCINDIDO'],
  ATIVO: ['INADIMPLENTE', 'INATIVO', 'TRANSFERIDO', 'RESCINDIDO'],
  INADIMPLENTE: ['ATIVO', 'INATIVO', 'RESCINDIDO'],
  INATIVO: ['ATIVO', 'RESCINDIDO'],
  TRANSFERIDO: [],
  RESCINDIDO: [],
};

/** Estes exigem motivo — ele fica na ficha e na auditoria. */
const NEEDS_REASON: MemberStatus[] = ['INADIMPLENTE', 'INATIVO', 'TRANSFERIDO', 'RESCINDIDO'];

const STATUS_ACTION_LABEL: Record<MemberStatus, string> = {
  CONTRATO_PENDENTE: 'Voltar a pendente',
  ATIVO: 'Ativar',
  INADIMPLENTE: 'Marcar inadimplente',
  INATIVO: 'Inativar',
  TRANSFERIDO: 'Transferir',
  RESCINDIDO: 'Rescindir',
};

/**
 * Associados do Clube AMAI Pass.
 *
 * O balcão vende (venda presencial), o Financeiro ativa: ATIVO exige o aceite
 * do contrato registrado e a permissão de aprovar. Rescindido e transferido
 * são finais — e devolvem a vaga ao lote.
 */
export function AssociadosPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [planId, setPlanId] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selling, setSelling] = useState(false);
  const [editing, setEditing] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [statusTarget, setStatusTarget] = useState<MemberStatus | null>(null);

  const plansQuery = useQuery({
    queryKey: ['member-plans'],
    queryFn: () => api.get<{ data: MemberPlan[] }>('/member-plans'),
  });

  const listQuery = useQuery({
    queryKey: ['members', { search, status, planId, page }],
    queryFn: () =>
      api.get<Paginated<Member>>('/members', {
        search: search || undefined,
        status: status || undefined,
        planId: planId || undefined,
        page,
        perPage: 25,
      }),
  });

  const detailQuery = useQuery({
    queryKey: ['member', selectedId],
    queryFn: () => api.get<Member>(`/members/${selectedId}`),
    enabled: Boolean(selectedId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['members'] });
    void queryClient.invalidateQueries({ queryKey: ['member'] });
    void queryClient.invalidateQueries({ queryKey: ['member-plans'] });
    void queryClient.invalidateQueries({ queryKey: ['financial-entries'] });
  };

  const acceptMutation = useMutation({
    mutationFn: (note: string) =>
      api.post<Member>(`/members/${selectedId}/accept-contract`, { note: note || null }),
    onSuccess: () => {
      setAccepting(false);
      refresh();
    },
  });

  const statusMutation = useMutation({
    mutationFn: (payload: { status: MemberStatus; reason?: string | null }) =>
      api.post<Member>(`/members/${selectedId}/status`, payload),
    onSuccess: () => {
      setStatusTarget(null);
      refresh();
    },
  });

  const member = detailQuery.data;
  const plans = plansQuery.data?.data ?? [];

  const columns: Array<Column<Member>> = [
    { key: 'registration', label: 'Matrícula', mono: true, strong: true, render: (row) => row.registration },
    { key: 'name', label: 'Titular', strong: true, render: (row) => row.name },
    {
      key: 'document',
      label: 'CPF',
      mono: true,
      render: (row) => (row.document ? formatDocument(row.document) : '—'),
    },
    { key: 'planName', label: 'Plano', render: (row) => blank(row.planName) },
    {
      key: 'status',
      label: 'Situação',
      render: (row) => (
        <StatusBadge kind={MEMBER_STATUS_TONE[row.status]}>{MEMBER_STATUS_LABELS[row.status]}</StatusBadge>
      ),
    },
    {
      key: 'renewalDate',
      label: 'Renovação',
      mono: true,
      render: (row) => (row.renewalDate ? formatDate(row.renewalDate) : '—'),
    },
    {
      key: 'openCents',
      label: 'Em aberto',
      right: true,
      mono: true,
      render: (row) =>
        row.openCents > 0 ? (
          <span style={{ color: T.amber, fontWeight: 600 }}>{formatCents(row.openCents)}</span>
        ) : (
          <span className="muted">—</span>
        ),
    },
  ];

  const hasFilters = Boolean(search || status || planId);
  const allowedTransitions = member ? TRANSITIONS[member.status] : [];

  return (
    <>
      <WorkspaceHeader
        title="Associados"
        actions={
          can('associados:criar') ? (
            <Button variant="primary" icon={Plus} onClick={() => setSelling(true)}>
              Venda presencial
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        <FilterBar
          placeholder="Buscar por matrícula, nome, CPF ou telefone…"
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          filters={[
            {
              key: 'status',
              label: 'Situação',
              value: status,
              options: MEMBER_STATUSES.map((value) => ({ value, label: MEMBER_STATUS_LABELS[value] })),
              onChange: (value) => {
                setStatus(value);
                setPage(1);
              },
            },
            {
              key: 'plan',
              label: 'Plano',
              value: planId,
              options: plans.map((plan) => ({ value: plan.id, label: `${plan.name} (lote ${plan.lotNumber})` })),
              onChange: (value) => {
                setPlanId(value);
                setPage(1);
              },
            },
          ]}
        />

        {listQuery.isPending && <TableSkeleton columns={7} />}
        {listQuery.isError && (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption="Associados do clube"
            columns={columns}
            rows={listQuery.data.data}
            meta={listQuery.data.meta}
            selectedId={selectedId}
            onRowClick={(row) => setSelectedId(row.id)}
            onPageChange={setPage}
            emptyState={
              hasFilters ? (
                <NoResultsState
                  onClear={() => {
                    setSearch('');
                    setStatus('');
                    setPlanId('');
                  }}
                />
              ) : (
                <EmptyState
                  title="Nenhum associado ainda"
                  description="A primeira venda presencial cria o cadastro, o contrato e a conta a receber de uma vez."
                  action={
                    can('associados:criar') ? (
                      <Button variant="primary" icon={Plus} onClick={() => setSelling(true)}>
                        Venda presencial
                      </Button>
                    ) : undefined
                  }
                />
              )
            }
          />
        )}
      </div>

      <ContextPanel
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        title={member ? `${member.registration} · ${member.name}` : 'Carregando…'}
        subtitle={member?.planName ?? undefined}
        tabs={['Ficha', 'Dependentes', 'Contrato']}
        footer={
          member ? (
            <>
              {!member.contractAcceptedAt &&
                member.status === 'CONTRATO_PENDENTE' &&
                can('associados:editar') && (
                  <Button small variant="primary" icon={FileSignature} onClick={() => setAccepting(true)}>
                    Registrar aceite
                  </Button>
                )}
              {can('associados:aprovar') &&
                allowedTransitions.map((target) => (
                  <Button
                    key={target}
                    small
                    variant={target === 'ATIVO' ? 'primary' : target === 'RESCINDIDO' ? 'danger' : 'outline'}
                    icon={target === 'ATIVO' ? Check : target === 'RESCINDIDO' ? Ban : undefined}
                    disabled={target === 'ATIVO' && !member.contractAcceptedAt}
                    title={
                      target === 'ATIVO' && !member.contractAcceptedAt
                        ? 'Registre o aceite do contrato antes de ativar'
                        : undefined
                    }
                    onClick={() => setStatusTarget(target)}
                  >
                    {STATUS_ACTION_LABEL[target]}
                  </Button>
                ))}
              {can('associados:editar') && (
                <Button small variant="ghost" icon={Pencil} onClick={() => setEditing(true)}>
                  Editar
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        {(tab) => {
          if (detailQuery.isPending) return <p className="muted">Carregando…</p>;
          if (!member) return <p className="muted">Não foi possível carregar.</p>;

          if (tab === 0) {
            return (
              <>
                <StatTiles
                  items={[
                    { label: 'Valor da adesão', value: formatCents(member.acquisitionCents) },
                    { label: 'Em aberto', value: formatCents(member.openCents) },
                  ]}
                />
                <ReadField
                  label="Situação"
                  value={
                    <StatusBadge kind={MEMBER_STATUS_TONE[member.status]}>
                      {MEMBER_STATUS_LABELS[member.status]}
                    </StatusBadge>
                  }
                />
                <ReadField label="CPF" value={member.document ? formatDocument(member.document) : '—'} mono />
                <ReadField label="Telefone" value={blank(member.phone)} mono />
                <ReadField label="E-mail" value={blank(member.email)} />
                <ReadField label="Início do plano" value={formatDate(member.planStartDate)} mono />
                <ReadField
                  label="Renovação"
                  value={member.renewalDate ? formatDate(member.renewalDate) : '—'}
                  mono
                />
                <ReadField
                  label="Dia de pagamento"
                  value={member.paymentDay ? `dia ${member.paymentDay}` : '—'}
                  mono
                />
                <ReadField
                  label="Manutenção"
                  value={
                    member.maintenanceFrequency === 'VITALICIA'
                      ? MAINTENANCE_FREQUENCY_LABELS.VITALICIA
                      : `${formatCents(member.maintenanceCents)} · ${MAINTENANCE_FREQUENCY_LABELS[member.maintenanceFrequency].toLowerCase()}`
                  }
                  mono
                />
                <ReadField
                  label="Cobrança automática"
                  value={
                    member.maintenanceFrequency === 'VITALICIA'
                      ? 'não se aplica'
                      : member.feesStartDate
                        ? `a partir de ${formatDate(member.feesStartDate)}`
                        : 'desligada'
                  }
                  mono
                />
                <ReadField label="Vendedor" value={blank(member.sellerName)} />
                <ReadField label="Lançamento da venda" value={blank(member.financialEntryCode)} mono />
                <ReadField
                  label="Aceite do contrato"
                  value={
                    member.contractAcceptedAt ? (
                      formatDateTime(member.contractAcceptedAt)
                    ) : (
                      <span style={{ color: T.amber }}>Pendente</span>
                    )
                  }
                  mono
                />
                {member.statusReason && <ReadField label="Motivo da situação" value={member.statusReason} />}
                {member.notes && <ReadField label="Observações" value={member.notes} />}
              </>
            );
          }

          if (tab === 1) {
            return (
              <>
                {member.dependents.length === 0 && (
                  <p className="muted" style={{ fontSize: 13 }}>
                    Nenhum dependente neste plano.
                  </p>
                )}
                {member.dependents.map((dependent) => (
                  <div key={dependent.id} className="card" style={{ padding: 12, marginBottom: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{dependent.name}</div>
                    <div className="mono" style={{ fontSize: 12, color: T.inkFaint, marginTop: 4 }}>
                      {DEPENDENT_TYPE_LABELS[dependent.type]}
                      {dependent.kinship ? ` · ${dependent.kinship}` : ''}
                      {dependent.birthDate ? ` · ${formatDate(dependent.birthDate)}` : ''}
                      {dependent.document ? ` · ${formatDocument(dependent.document)}` : ''}
                    </div>
                  </div>
                ))}
              </>
            );
          }

          return (
            <>
              {member.contractText ? (
                <pre
                  className="mono"
                  style={{
                    whiteSpace: 'pre-wrap',
                    fontSize: 12.5,
                    lineHeight: 1.6,
                    background: T.surface,
                    border: `1px solid ${T.line}`,
                    borderRadius: 8,
                    padding: 14,
                  }}
                >
                  {member.contractText}
                </pre>
              ) : (
                <p className="muted" style={{ fontSize: 13 }}>
                  O plano não tinha template de contrato na data da venda.
                </p>
              )}
              <p className="muted" style={{ fontSize: 12.5 }}>
                {member.contractAcceptedAt
                  ? `Aceito em ${formatDateTime(member.contractAcceptedAt)}.`
                  : 'Aceite ainda não registrado — sem ele o associado não pode ser ativado.'}
              </p>
            </>
          );
        }}
      </ContextPanel>

      <AcceptContractModal
        open={accepting}
        loading={acceptMutation.isPending}
        error={acceptMutation.error}
        onClose={() => {
          setAccepting(false);
          acceptMutation.reset();
        }}
        onConfirm={(note) => acceptMutation.mutate(note)}
      />

      <ConfirmDialog
        open={Boolean(statusTarget)}
        destructive={statusTarget === 'RESCINDIDO' || statusTarget === 'TRANSFERIDO'}
        requireReason={Boolean(statusTarget && NEEDS_REASON.includes(statusTarget))}
        loading={statusMutation.isPending}
        title={statusTarget ? `${STATUS_ACTION_LABEL[statusTarget]}?` : ''}
        confirmLabel={statusTarget ? STATUS_ACTION_LABEL[statusTarget] : 'Confirmar'}
        message={
          <>
            {statusTarget === 'ATIVO' &&
              'O associado passa a valer na catraca. A conta a receber da venda segue no Financeiro.'}
            {statusTarget === 'RESCINDIDO' &&
              'Rescisão é FINAL: não volta atrás e a vaga retorna ao lote. Valores já pagos ficam no Financeiro — estorno é por lá.'}
            {statusTarget === 'TRANSFERIDO' &&
              'Transferência é FINAL para este vínculo — o novo titular entra por uma nova venda.'}
            {statusTarget === 'INADIMPLENTE' &&
              'Marca o associado como inadimplente. Ele volta a ativo quando regularizar.'}
            {statusTarget === 'INATIVO' && 'Suspende o acesso sem encerrar o vínculo.'}
            {statusMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(statusMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setStatusTarget(null);
          statusMutation.reset();
        }}
        onConfirm={(reason) =>
          statusTarget && statusMutation.mutate({ status: statusTarget, reason: reason || null })
        }
      />

      {member && (
        <MemberEditDrawer
          open={editing}
          member={member}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            refresh();
          }}
        />
      )}

      <SaleDrawer
        open={selling}
        plans={plans}
        onClose={() => setSelling(false)}
        onCreated={(id) => {
          setSelling(false);
          setSelectedId(id);
          refresh();
        }}
      />
    </>
  );
}

function AcceptContractModal({
  open,
  loading,
  error,
  onClose,
  onConfirm,
}: {
  open: boolean;
  loading: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: (note: string) => void;
}) {
  const [note, setNote] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setNote('');
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Registrar aceite do contrato"
      description="Registre COMO o aceite aconteceu — assinatura impressa, aceite no balcão…"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="primary" icon={FileSignature} loading={loading} onClick={() => onConfirm(note.trim())}>
            Registrar aceite
          </Button>
        </>
      }
    >
      {Boolean(error) && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(error)}
        </div>
      )}
      <FormSection>
        <Field label="Como foi o aceite" htmlFor="accept-note">
          <input
            id="accept-note"
            className="input"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Contrato impresso assinado em 2 vias no balcão"
          />
        </Field>
      </FormSection>
    </Modal>
  );
}

/* ------------------------- dependentes (repetidor) ------------------------ */

type DependentDraft = {
  name: string;
  document: string;
  birthDate: string;
  kinship: string;
  type: (typeof DEPENDENT_TYPES)[number];
};

const emptyDependent = (): DependentDraft => ({
  name: '',
  document: '',
  birthDate: '',
  kinship: '',
  type: 'ADULTO',
});

function dependentsPayload(drafts: DependentDraft[]): MemberDependentInput[] {
  return drafts
    .filter((draft) => draft.name.trim().length >= 2)
    .map((draft) => ({
      name: draft.name.trim(),
      document: draft.document ? onlyDigits(draft.document) : null,
      birthDate: draft.birthDate || null,
      kinship: draft.kinship.trim() || null,
      type: draft.type,
    }));
}

function DependentsEditor({
  drafts,
  onChange,
  max,
}: {
  drafts: DependentDraft[];
  onChange: (next: DependentDraft[]) => void;
  max?: number;
}) {
  const update = (index: number, patch: Partial<DependentDraft>) =>
    onChange(drafts.map((draft, i) => (i === index ? { ...draft, ...patch } : draft)));

  return (
    <>
      {drafts.map((draft, index) => (
        <div key={index} className="card" style={{ padding: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="input"
              value={draft.name}
              onChange={(event) => update(index, { name: event.target.value })}
              placeholder={`Dependente ${index + 1} — nome completo`}
              aria-label={`Nome do dependente ${index + 1}`}
              style={{ flex: 1 }}
            />
            <Button
              small
              variant="ghost"
              icon={Trash2}
              aria-label={`Remover dependente ${index + 1}`}
              onClick={() => onChange(drafts.filter((_, i) => i !== index))}
            />
          </div>
          <div className="grid-2" style={{ marginTop: 8 }}>
            <input
              className="input input--mono"
              value={draft.document}
              onChange={(event) => update(index, { document: event.target.value })}
              placeholder="CPF (opcional)"
              aria-label={`CPF do dependente ${index + 1}`}
            />
            <DateInput
              className="input input--mono"
              value={draft.birthDate}
              onChange={(iso) => update(index, { birthDate: iso })}
              aria-label={`Nascimento do dependente ${index + 1}`}
            />
          </div>
          <div className="grid-2" style={{ marginTop: 8 }}>
            <input
              className="input"
              value={draft.kinship}
              onChange={(event) => update(index, { kinship: event.target.value })}
              placeholder="Parentesco (cônjuge, filho…)"
              aria-label={`Parentesco do dependente ${index + 1}`}
            />
            <select
              className="input"
              value={draft.type}
              onChange={(event) => update(index, { type: event.target.value as DependentDraft['type'] })}
              aria-label={`Tipo do dependente ${index + 1}`}
            >
              {DEPENDENT_TYPES.map((value) => (
                <option key={value} value={value}>
                  {DEPENDENT_TYPE_LABELS[value]}
                </option>
              ))}
            </select>
          </div>
        </div>
      ))}
      <Button
        small
        variant="outline"
        icon={Plus}
        disabled={max !== undefined && drafts.length >= max}
        onClick={() => onChange([...drafts, emptyDependent()])}
      >
        Adicionar dependente
      </Button>
      {max !== undefined && drafts.length >= max && (
        <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
          O plano cobre até {max + 1} pessoa(s) contando o titular.
        </p>
      )}
    </>
  );
}

/* ----------------------------- editar ficha ------------------------------ */

function MemberEditDrawer({
  open,
  member,
  onClose,
  onSaved,
}: {
  open: boolean;
  member: Member;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [paymentDay, setPaymentDay] = useState('');
  const [renewalDate, setRenewalDate] = useState('');
  const [feesStartDate, setFeesStartDate] = useState('');
  const [notes, setNotes] = useState('');
  const [dependents, setDependents] = useState<DependentDraft[]>([]);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const formKey = open ? member.id : null;
  if (formKey !== loadedFor) {
    setLoadedFor(formKey);
    setPaymentDay(member.paymentDay ? String(member.paymentDay) : '');
    setRenewalDate(member.renewalDate ? member.renewalDate.slice(0, 10) : '');
    setFeesStartDate(member.feesStartDate ? member.feesStartDate.slice(0, 10) : '');
    setNotes(member.notes ?? '');
    setDependents(
      member.dependents.map((dependent) => ({
        name: dependent.name,
        document: dependent.document ?? '',
        birthDate: dependent.birthDate ? dependent.birthDate.slice(0, 10) : '',
        kinship: dependent.kinship ?? '',
        type: dependent.type,
      })),
    );
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.patch<Member>(`/members/${member.id}`, body),
    onSuccess: onSaved,
  });

  const errors = fieldErrors(mutation.error);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`Editar ${member.registration}`}
      description="Valores da venda são congelados — aqui só a ficha e os dependentes"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                paymentDay: paymentDay ? Number(paymentDay) : null,
                renewalDate: renewalDate || null,
                feesStartDate: feesStartDate || null,
                notes: notes.trim() || null,
                dependents: dependentsPayload(dependents),
              })
            }
          >
            Salvar
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <div className="grid-2">
          <Field label="Dia de pagamento" htmlFor="edit-payday" error={errors.paymentDay} hint="1 a 28">
            <input
              id="edit-payday"
              className="input input--mono"
              inputMode="numeric"
              value={paymentDay}
              onChange={(event) => setPaymentDay(event.target.value.replace(/\D/g, '').slice(0, 2))}
            />
          </Field>
          <Field label="Data de renovação" htmlFor="edit-renewal" error={errors.renewalDate}>
            <DateInput
              id="edit-renewal"
              className="input input--mono"
              value={renewalDate}
              onChange={(iso) => setRenewalDate(iso)}
            />
          </Field>
        </div>
        <Field
          label="Manutenção a partir de"
          htmlFor="edit-fees-start"
          error={errors.feesStartDate}
          hint="Em branco: este associado NÃO é cobrado automaticamente"
        >
          <DateInput
            id="edit-fees-start"
            className="input input--mono"
            value={feesStartDate}
            onChange={(iso) => setFeesStartDate(iso)}
          />
        </Field>
        <Field label="Observações" htmlFor="edit-notes">
          <textarea
            id="edit-notes"
            className="input"
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>
      </FormSection>

      <CollapsibleSection label="Dependentes" defaultOpen>
        <DependentsEditor drafts={dependents} onChange={setDependents} />
      </CollapsibleSection>
    </Drawer>
  );
}

/* ---------------------------- venda presencial --------------------------- */

type PaymentDraft = { method: PaymentMethod; amountCents: number | null; bankAccountId: string };

function SaleDrawer({
  open,
  plans,
  onClose,
  onCreated,
}: {
  open: boolean;
  plans: MemberPlan[];
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const bankAccounts = useBankAccounts();

  const [planId, setPlanId] = useState('');
  const [document, setDocument] = useState('');
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [uf, setUf] = useState('AC');
  const [planStartDate, setPlanStartDate] = useState(todayISO());
  const [paymentDay, setPaymentDay] = useState('');
  const [dependents, setDependents] = useState<DependentDraft[]>([]);
  const [payments, setPayments] = useState<PaymentDraft[]>([]);
  const [notes, setNotes] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setPlanId('');
      setDocument('');
      setName('');
      setBirthDate('');
      setPhone('');
      setWhatsapp('');
      setEmail('');
      setCity('');
      setUf('AC');
      setPlanStartDate(todayISO());
      setPaymentDay('');
      setDependents([]);
      setPayments([]);
      setNotes('');
    }
  }

  const digits = onlyDigits(document);

  // CPF completo dispara a busca: se a pessoa já existe, a venda reaproveita o
  // cadastro (mesmo CPF nunca vira duas pessoas).
  const personQuery = useQuery({
    queryKey: ['people', 'by-document', digits],
    queryFn: () => api.get<Paginated<Person>>('/people', { search: digits, perPage: 5 }),
    enabled: open && digits.length === 11,
    staleTime: 30_000,
  });

  const matchedPerson =
    digits.length === 11
      ? (personQuery.data?.data ?? []).find((person) => person.document === digits)
      : undefined;

  const plan = plans.find((item) => item.id === planId);
  const availablePlans = plans.filter((item) => item.isActive);
  const soldOut = (item: MemberPlan) => item.quota > 0 && item.soldCount >= item.quota;

  const totalCents = plan ? plan.acquisitionCents + plan.dependentCents * dependents.length : 0;
  const paidCents = payments.reduce((acc, payment) => acc + (payment.amountCents ?? 0), 0);
  const openCents = totalCents - paidCents;

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post<Member>('/members/sell', body),
    onSuccess: (created) => onCreated(created.id),
  });

  const errors = fieldErrors(mutation.error);

  const validPayments = payments.filter((payment) => (payment.amountCents ?? 0) > 0);
  const paymentsComplete = validPayments.every((payment) => payment.bankAccountId);
  const canSubmit =
    Boolean(plan) &&
    digits.length === 11 &&
    (matchedPerson || name.trim().length >= 2) &&
    Boolean(planStartDate) &&
    paidCents <= totalCents &&
    paymentsComplete;

  const submit = () =>
    mutation.mutate({
      planId,
      personId: matchedPerson?.id ?? null,
      person: matchedPerson
        ? null
        : {
            name: name.trim(),
            document: digits,
            birthDate: birthDate || null,
            phone: phone.trim() || null,
            whatsapp: whatsapp.trim() || null,
            email: email.trim() || null,
            city: city.trim() || null,
            uf: uf.trim().toUpperCase() || null,
          },
      planStartDate,
      paymentDay: paymentDay ? Number(paymentDay) : null,
      dependents: dependentsPayload(dependents),
      payments: validPayments.map((payment) => ({
        method: payment.method,
        amountCents: payment.amountCents,
        bankAccountId: payment.bankAccountId,
      })),
      notes: notes.trim() || null,
    });

  const updatePayment = (index: number, patch: Partial<PaymentDraft>) =>
    setPayments(payments.map((payment, i) => (i === index ? { ...payment, ...patch } : payment)));

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Venda presencial"
      description="Uma tela só: cadastro, contrato e conta a receber nascem juntos"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button variant="primary" loading={mutation.isPending} disabled={!canSubmit} onClick={submit}>
            Efetuar venda{plan ? ` · ${formatCents(totalCents)}` : ''}
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection title="Plano">
        <Field label="Plano do lote vigente" htmlFor="sale-plan" error={errors.planId}>
          <select
            id="sale-plan"
            className="input"
            value={planId}
            onChange={(event) => setPlanId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {availablePlans.map((item) => (
              <option key={item.id} value={item.id} disabled={soldOut(item)}>
                {item.name} · lote {item.lotNumber} · {formatCents(item.acquisitionCents)}
                {soldOut(item)
                  ? ' — ESGOTADO'
                  : item.quota > 0
                    ? ` (${item.quota - item.soldCount} vaga(s))`
                    : ''}
              </option>
            ))}
          </select>
        </Field>
      </FormSection>

      <FormSection title="Titular">
        <Field
          label="CPF do titular"
          htmlFor="sale-cpf"
          error={errors.personId ?? errors['person.document']}
          hint="Se o CPF já tem cadastro, a venda usa a pessoa existente"
        >
          <input
            id="sale-cpf"
            className="input input--mono"
            inputMode="numeric"
            value={document}
            onChange={(event) => setDocument(event.target.value)}
            placeholder="000.000.000-00"
          />
        </Field>

        {matchedPerson ? (
          <div
            className="card"
            style={{ padding: 12, borderLeft: `3px solid ${T.mare}`, marginBottom: 4 }}
          >
            <div style={{ fontWeight: 600, fontSize: 13 }}>{matchedPerson.name}</div>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
              Cadastro existente — a venda entra neste CPF, sem duplicar pessoa.
            </div>
          </div>
        ) : (
          digits.length === 11 && (
            <>
              <Field label="Nome completo" htmlFor="sale-name" error={errors['person.name']}>
                <input
                  id="sale-name"
                  className="input"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </Field>
              <div className="grid-2">
                <Field label="Nascimento" htmlFor="sale-birth">
                  <DateInput
                    id="sale-birth"
                    className="input input--mono"
                    value={birthDate}
                    onChange={(iso) => setBirthDate(iso)}
                  />
                </Field>
                <Field label="WhatsApp" htmlFor="sale-whats">
                  <input
                    id="sale-whats"
                    className="input input--mono"
                    value={whatsapp}
                    onChange={(event) => setWhatsapp(event.target.value)}
                    placeholder="(68) 9…"
                  />
                </Field>
              </div>
              <div className="grid-2">
                <Field label="Telefone" htmlFor="sale-phone">
                  <input
                    id="sale-phone"
                    className="input input--mono"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                  />
                </Field>
                <Field label="E-mail" htmlFor="sale-email" error={errors['person.email']}>
                  <input
                    id="sale-email"
                    className="input"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </Field>
              </div>
              <div className="grid-2">
                <Field label="Cidade" htmlFor="sale-city">
                  <input
                    id="sale-city"
                    className="input"
                    value={city}
                    onChange={(event) => setCity(event.target.value)}
                    placeholder="Senador Guiomard"
                  />
                </Field>
                <Field label="UF" htmlFor="sale-uf">
                  <input
                    id="sale-uf"
                    className="input input--mono"
                    maxLength={2}
                    value={uf}
                    onChange={(event) => setUf(event.target.value.toUpperCase())}
                  />
                </Field>
              </div>
            </>
          )
        )}

        <div className="grid-2">
          <Field label="Início do plano" htmlFor="sale-start" error={errors.planStartDate}>
            <DateInput
              id="sale-start"
              className="input input--mono"
              value={planStartDate}
              onChange={(iso) => setPlanStartDate(iso)}
            />
          </Field>
          <Field label="Dia de pagamento" htmlFor="sale-payday" hint="1 a 28, opcional">
            <input
              id="sale-payday"
              className="input input--mono"
              inputMode="numeric"
              value={paymentDay}
              onChange={(event) => setPaymentDay(event.target.value.replace(/\D/g, '').slice(0, 2))}
            />
          </Field>
        </div>
      </FormSection>

      <FormSection title="Dependentes">
        {plan && plan.dependentCents > 0 && (
          <p className="muted" style={{ fontSize: 12.5, marginTop: -4 }}>
            Cada dependente soma {formatCents(plan.dependentCents)} ao valor da venda.
          </p>
        )}
        {errors.dependents && (
          <p style={{ color: T.red, fontSize: 12.5 }}>{errors.dependents}</p>
        )}
        <DependentsEditor
          drafts={dependents}
          onChange={setDependents}
          max={plan ? Math.max(0, plan.maxPeople - 1) : undefined}
        />
      </FormSection>

      <FormSection title="Recebimentos">
        <p className="muted" style={{ fontSize: 12.5, marginTop: -4 }}>
          Pagamento misto é a regra: quantas linhas precisar. O que não entrar agora fica em aberto
          no contas a receber.
        </p>
        {errors.payments && <p style={{ color: T.red, fontSize: 12.5 }}>{errors.payments}</p>}
        {payments.map((payment, index) => (
          <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <select
              className="input"
              value={payment.method}
              onChange={(event) => updatePayment(index, { method: event.target.value as PaymentMethod })}
              aria-label={`Forma do recebimento ${index + 1}`}
              style={{ width: 150 }}
            >
              {PAYMENT_METHODS.map((method) => (
                <option key={method} value={method}>
                  {PAYMENT_METHOD_LABELS[method]}
                </option>
              ))}
            </select>
            <MoneyInput
              aria-label={`Valor do recebimento ${index + 1}`}
              value={payment.amountCents}
              onChange={(value) => updatePayment(index, { amountCents: value })}
            />
            <select
              className="input"
              value={payment.bankAccountId}
              onChange={(event) => updatePayment(index, { bankAccountId: event.target.value })}
              aria-label={`Conta do recebimento ${index + 1}`}
              style={{ flex: 1 }}
            >
              <option value="">Conta…</option>
              {(bankAccounts.data ?? []).map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
            <Button
              small
              variant="ghost"
              icon={Trash2}
              aria-label={`Remover recebimento ${index + 1}`}
              onClick={() => setPayments(payments.filter((_, i) => i !== index))}
            />
          </div>
        ))}
        <Button
          small
          variant="outline"
          icon={Plus}
          onClick={() =>
            setPayments([...payments, { method: 'PIX', amountCents: null, bankAccountId: '' }])
          }
        >
          Adicionar recebimento
        </Button>

        {plan && (
          <div
            className="mono"
            style={{
              marginTop: 12,
              padding: 12,
              background: T.surface,
              border: `1px solid ${T.line}`,
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            Total da venda <strong>{formatCents(totalCents)}</strong> · recebido agora{' '}
            {formatCents(paidCents)} ·{' '}
            {openCents > 0 ? (
              <span style={{ color: T.amber }}>fica em aberto {formatCents(openCents)}</span>
            ) : openCents === 0 ? (
              <span style={{ color: T.green }}>quitado no ato</span>
            ) : (
              <span style={{ color: T.red }}>recebido passa do total — confira os valores</span>
            )}
          </div>
        )}
      </FormSection>

      <CollapsibleSection label="Observações">
        <Field label="Observações internas" htmlFor="sale-notes">
          <textarea
            id="sale-notes"
            className="input"
            rows={2}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>
      </CollapsibleSection>
    </Drawer>
  );
}
